// src/app/useSession.ts
import { useState, useEffect } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'

type SessionTokens = {
  access_token: string
  refresh_token: string
}

// Global guard so multiple tool components don’t all spam the parent
declare global {
  interface Window {
    __MMS_TOKENS_REQUESTED__?: boolean
  }
}

const VERBOSE = false // flip to true while debugging in dev

let sessionPromiseResolve: (() => void) | null = null
let sessionPromiseReject: (() => void) | null = null
let sessionPromise: Promise<void> | null = null

export function waitForSession(): Promise<void> {
  if (sessionPromise) return sessionPromise
  sessionPromise = new Promise<void>((resolve, reject) => {
    sessionPromiseResolve = resolve
    sessionPromiseReject = reject
    setTimeout(() => {
      if (sessionPromiseReject) {
        sessionPromiseReject()
        sessionPromiseResolve = null
        sessionPromiseReject = null
        sessionPromise = null
      }
    }, 5000)
  })
  return sessionPromise
}

function scrubTokensFromUrl() {
  try {
    const url = new URL(window.location.href)
    let changed = false

    ;['access_token', 'refresh_token', 'token'].forEach(k => {
      if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true }
    })

    if (url.hash.includes('?')) {
      const [hashPath, qs] = url.hash.split('?')
      const h = new URLSearchParams(qs)
      ;['access_token', 'refresh_token', 'token'].forEach(k => h.delete(k))
      const newHash = h.toString() ? `${hashPath}?${h.toString()}` : hashPath
      if (newHash !== url.hash) { url.hash = newHash; changed = true }
    }

    if (changed) {
      window.history.replaceState(null, '', url.toString())
    }
  } catch {
    // ignore URL parsing issues
  }
}

async function setSessionFromTokens(tokens: SessionTokens): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.setSession(tokens)
    if (error) {
      if (import.meta.env.DEV && VERBOSE) console.warn('[useSession] setSession error:', error)
      return false
    }
    return !!data.session
  } catch (err) {
    if (import.meta.env.DEV && VERBOSE) console.warn('[useSession] setSession exception:', err)
    return false
  }
}

export function useSession() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    let lastAccessToken: string | null = null

    const resolveAndClear = () => {
      if (sessionPromiseResolve) sessionPromiseResolve()
      sessionPromiseResolve = null
      sessionPromiseReject = null
      sessionPromise = null
    }
    const rejectAndClear = () => {
      if (sessionPromiseReject) sessionPromiseReject()
      sessionPromiseResolve = null
      sessionPromiseReject = null
      sessionPromise = null
    }

    const requestTokensFromParent = () => {
      if (window.__MMS_TOKENS_REQUESTED__) return
      window.__MMS_TOKENS_REQUESTED__ = true
      if (import.meta.env.DEV && VERBOSE) console.log('[useSession] Requesting tokens from parent')
      // reply-only flow on parent; we just request
      window.parent?.postMessage({ type: 'mms:requestSession' }, '*')
    }

    const initialize = async () => {
      try {
        const { data: { session: existing } } = await supabase.auth.getSession()
        if (mounted && existing) {
          setSession(existing)
          lastAccessToken = existing.access_token ?? null
          setLoading(false)
          resolveAndClear()
          scrubTokensFromUrl()
          return
        }
        requestTokensFromParent()
        if (mounted) {
          setLoading(false)
          rejectAndClear()
        }
      } catch (err) {
        if (import.meta.env.DEV && VERBOSE) console.warn('[useSession] initialize error:', err)
        requestTokensFromParent()
        if (mounted) {
          setLoading(false)
          rejectAndClear()
        }
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return
      // Avoid redundant state sets & log spam: only update if token actually changed
      const nextToken = s?.access_token ?? null
      if (nextToken && nextToken === lastAccessToken) return
      lastAccessToken = nextToken
      setSession(s)
      setLoading(false)
      if (s) {
        if (import.meta.env.DEV && VERBOSE) console.log('[useSession] Session updated via onAuthStateChange')
        resolveAndClear()
      }
    })

    const handleMessage = async (event: MessageEvent) => {
      const d: any = event?.data
      if (!d || d.type !== 'mms:setSession') return

      // Support both old and new payload shapes
      const access_token  = d?.payload?.access_token ?? d?.access_token
      const refresh_token = d?.payload?.refresh_token ?? d?.refresh_token
      if (!access_token || !refresh_token) {
        // silently ignore incomplete payloads
        return
      }
      const ok = await setSessionFromTokens({ access_token, refresh_token })
      if (ok) {
        if (import.meta.env.DEV && VERBOSE) console.log('[useSession] Session set via postMessage')
        scrubTokensFromUrl()
      }
    }

    window.addEventListener('message', handleMessage)
    initialize()

    return () => {
      mounted = false
      subscription.unsubscribe()
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  return {
    session,
    loading,
    currentUserId: session?.user?.id || null,
  }
}